import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import axios from "../../../../api/axios";
import { toast } from "react-hot-toast";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  PencilSquareIcon,
  WrenchScrewdriverIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  DocumentDuplicateIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import {
  useProjectPermissions,
  useProjectWorkOrdersAccess,
} from "../../../../routes/roleOnly";
import { debounce } from "lodash";
import {
  ProjectActivityEditDialog,
  PROJECT_ACTIVITY_STATUSES,
} from "../project-activity-edit-dialog";
import { ProjectActivityGroupDialog } from "../project-activity-group-dialog";
import { ProjectActivityGroupDuplicateDialog } from "../project-activity-group-duplicate-dialog";
import { projectActivityLine } from "../../hours/projectActivityDisplay";
import {
  deleteProjectActivityGroup,
  duplicateProjectActivity,
  exportProjectActivitiesPdf,
  fetchProjectActivityGroups,
  reorderProjectActivities,
  reorderProjectActivityGroups,
} from "../../../../api/project_activity";
import {
  activityDroppableId,
  buildActivityGroupSections,
  hasRealActivityGroups,
  inferGroupsFromActivities,
  parseActivityDroppableId,
  resolveActivityGroupId,
  sortByRowOrder,
  UNGROUPED_KEY,
} from "../../../utils/moduleSelectors/projectActivityGroupUtils";

const emptyDraft = () => ({
  title: "",
  description: "",
  budget_hours: "",
  budget_fee: "",
  is_additional: false,
});

function nextActivityNumber(savedRows) {
  let max = 0;
  for (const r of savedRows) {
    if (!r?.id) continue;
    const raw = String(r.number ?? "").replace(/[^\d]/g, "");
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n) && n > max) max = n;
  }
  return String(max + 1);
}

function normNum(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function statusBadgeClass(status) {
  switch (status) {
    case "completed":
      return "bg-emerald-100 text-emerald-800";
    case "in_progress":
      return "bg-blue-100 text-blue-800";
    case "on_hold":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function hoursPercent(row) {
  const logged = Number(row.logged_hours) || 0;
  const budget = row.budget_hours != null ? Number(row.budget_hours) : null;
  if (budget != null && budget > 0) {
    return Math.min(100, (logged / budget) * 100);
  }
  return null;
}

function DragHandle({ dragHandleProps, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      className="inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px]"
      {...dragHandleProps}
    >
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <circle cx="9" cy="7" r="1.5" />
        <circle cx="15" cy="7" r="1.5" />
        <circle cx="9" cy="12" r="1.5" />
        <circle cx="15" cy="12" r="1.5" />
        <circle cx="9" cy="17" r="1.5" />
        <circle cx="15" cy="17" r="1.5" />
      </svg>
    </button>
  );
}

function reindexActivities(list) {
  const byGroup = new Map();
  for (const row of list) {
    const key = activityDroppableId(resolveActivityGroupId(row));
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key).push(row);
  }
  const next = [];
  for (const rows of byGroup.values()) {
    rows.forEach((row, index) => {
      next.push({
        ...row,
        row_order: index,
        group_id: resolveActivityGroupId(row),
      });
    });
  }
  return next;
}

export const ProjectActivitiesTab = ({
  project,
  /** Tab index on project page for «Timeregistrering» — keep in sync with `project.js` tabs order */
  timeRegistrationTabIndex = 10,
  /** Tab index for «Arbeidsordre» on project page */
  workOrdersTabIndex = 3,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const projectId = project?.id;
  const projectPermissions = useProjectPermissions(project);
  const workOrderAccess = useProjectWorkOrdersAccess(project);
  const canManage = projectPermissions?.canEdit || projectPermissions?.isAdmin;

  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState(() => new Set());
  const [draft, setDraft] = useState(emptyDraft);
  const [draftGroupId, setDraftGroupId] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterKind, setFilterKind] = useState("all");
  const savingDraftRef = useRef(false);
  const draftTitleRef = useRef(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogActivity, setDialogActivity] = useState(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [dialogGroup, setDialogGroup] = useState(null);
  const [groupDupOpen, setGroupDupOpen] = useState(false);
  const [groupDupSource, setGroupDupSource] = useState(null);
  const [duplicatingActivityId, setDuplicatingActivityId] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((v) => {
        setDebouncedSearch(v);
      }, 200),
    []
  );

  useEffect(() => {
    debouncedSetSearch(search);
    return () => debouncedSetSearch.cancel();
  }, [search, debouncedSetSearch]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/projects/${projectId}/project_activities`);
      const raw = res.data?.data;
      const activityRows = Array.isArray(raw) ? raw : [];
      setItems(activityRows);

      try {
        const fetchedGroups = await fetchProjectActivityGroups(projectId);
        setGroups(Array.isArray(fetchedGroups) ? fetchedGroups : []);
      } catch (groupError) {
        console.error(groupError);
        toast.error(t("project.activities.group_load_error"));
        setGroups(inferGroupsFromActivities(activityRows));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("project.activities.error_loading"));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredItems = useMemo(() => {
    let list = items;
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const title = (r.title || "").toLowerCase();
        const num = (r.number || "").toLowerCase();
        const desc = (r.description || "").toLowerCase();
        const disp = (r.display_title || "").toLowerCase();
        return (
          title.includes(q) ||
          num.includes(q) ||
          desc.includes(q) ||
          disp.includes(q)
        );
      });
    }
    if (filterKind === "additional") list = list.filter((r) => r.is_additional);
    if (filterKind === "standard") list = list.filter((r) => !r.is_additional);
    return list;
  }, [items, debouncedSearch, filterKind]);

  const showGroups = hasRealActivityGroups(items, groups);
  const groupedSections = useMemo(
    () =>
      buildActivityGroupSections(filteredItems, groups, {
        ungroupedLabel: t("project_activity.ungrouped"),
        hideEmptyGroups: false,
        includeEmptyUngrouped: showGroups,
      }),
    [filteredItems, groups, t, showGroups]
  );
  const realGroupSections = groupedSections.filter((s) => !s.isUngrouped);
  const ungroupedSection = groupedSections.find((s) => s.isUngrouped) || {
    key: UNGROUPED_KEY,
    id: null,
    title: t("project_activity.ungrouped"),
    activities: [],
    isUngrouped: true,
  };
  const dndEnabled =
    canManage &&
    editMode &&
    !debouncedSearch.trim() &&
    filterKind === "all";

  const aggregates = useMemo(() => {
    let totalBudgetHours = 0;
    let hasBudgetHours = false;
    let totalLogged = 0;
    let totalBudgetFee = 0;
    let hasFee = false;
    let completed = 0;
    for (const r of items) {
      totalLogged += Number(r.logged_hours) || 0;
      if (r.budget_hours != null && r.budget_hours !== "") {
        const bh = Number(r.budget_hours);
        if (Number.isFinite(bh)) {
          totalBudgetHours += bh;
          hasBudgetHours = true;
        }
      }
      if (r.budget_fee != null && r.budget_fee !== "") {
        const bf = Number(r.budget_fee);
        if (Number.isFinite(bf)) {
          totalBudgetFee += bf;
          hasFee = true;
        }
      }
      if (r.status === "completed") completed += 1;
    }
    const total = items.length;
    const hoursBarPct =
      hasBudgetHours && totalBudgetHours > 0
        ? Math.min(100, (totalLogged / totalBudgetHours) * 100)
        : null;
    const doneBarPct = total > 0 ? (completed / total) * 100 : 0;
    return {
      totalBudgetHours,
      hasBudgetHours,
      totalLogged,
      totalBudgetFee,
      hasFee,
      completed,
      total,
      hoursBarPct,
      doneBarPct,
    };
  }, [items]);

  const focusDraftTitle = () => {
    requestAnimationFrame(() => draftTitleRef.current?.focus());
  };

  const saveDraftFromTitleBlur = async () => {
    if (!canManage || !editMode || !projectId) return;
    const title = draft.title.trim();
    if (!title || savingDraftRef.current) return;

    savingDraftRef.current = true;
    try {
      const number = nextActivityNumber(items);
      const payload = {
        title,
        number,
        description: draft.description?.trim() || null,
        budget_hours: normNum(draft.budget_hours),
        budget_fee: normNum(draft.budget_fee),
        is_additional: !!draft.is_additional,
        activity_type: "standard",
        status: "open",
        group_id: draftGroupId,
      };

      const res = await axios.post(`/projects/${projectId}/project_activities`, {
        project_activity: payload,
      });

      if (!res.data?.success) {
        throw new Error((res.data?.errors || []).join(", ") || "create failed");
      }

      const created = res.data.data;
      setItems((prev) =>
        [...prev, created].sort(
          (a, b) =>
            (a.row_order || 0) - (b.row_order || 0) || (a.id || 0) - (b.id || 0)
        )
      );
      setDraft(emptyDraft());
      toast.success(t("project.activities.created"));
      focusDraftTitle();
      await load();
    } catch (e) {
      console.error(e);
      toast.error(t("project.activities.save_error"));
    } finally {
      savingDraftRef.current = false;
    }
  };

  const patchRow = async (row, partial) => {
    if (!canManage || !projectId || !row?.id) return;
    const keys = Object.keys(partial);
    if (!keys.length) return;
    try {
      const res = await axios.patch(
        `/projects/${projectId}/project_activities/${row.id}`,
        {
          project_activity: partial,
        }
      );
      if (!res.data?.success) {
        throw new Error((res.data?.errors || []).join(", "));
      }
      const updated = res.data.data;
      setItems((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                ...updated,
                logged_hours:
                  updated.logged_hours != null
                    ? updated.logged_hours
                    : r.logged_hours,
                linked_work_orders_count:
                  updated.linked_work_orders_count != null
                    ? updated.linked_work_orders_count
                    : r.linked_work_orders_count,
              }
            : r
        )
      );
    } catch (e) {
      console.error(e);
      toast.error(t("project.activities.save_error"));
      await load();
    }
  };

  const handleDelete = async (row) => {
    if (!canManage || !window.confirm(t("project.activities.delete_confirm")))
      return;
    try {
      await axios.delete(`/projects/${projectId}/project_activities/${row.id}`);
      setItems((prev) => prev.filter((r) => r.id !== row.id));
      toast.success(t("project.activities.deleted_toast"));
    } catch (e) {
      console.error(e);
      toast.error(t("project.activities.delete_error"));
    }
  };

  const openEditDialog = (row) => {
    setDialogActivity(row);
    setDialogOpen(true);
  };

  const onDialogSaved = (updated) => {
    if (updated?.id) {
      setItems((prev) =>
        prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
      );
    }
    load();
  };

  const persistActivityOrder = async (nextItems) => {
    const payload = nextItems
      .filter((row) => row?.id)
      .map((row) => ({
        id: row.id,
        group_id: resolveActivityGroupId(row),
        row_order: row.row_order ?? 0,
      }));
    try {
      await reorderProjectActivities(projectId, payload);
    } catch (error) {
      console.error(error);
      toast.error(t("project.activities.group_reorder_error"));
      await load();
    }
  };

  const handleDragEnd = async (result) => {
    if (!dndEnabled || !result.destination) return;

    if (result.type === "GROUP") {
      const nextIds = realGroupSections.map((section) => section.id);
      const [moved] = nextIds.splice(result.source.index, 1);
      nextIds.splice(result.destination.index, 0, moved);
      const ordered = nextIds.map((id, index) => {
        const group = groups.find((g) => String(g.id) === String(id));
        return { ...(group || { id }), row_order: index };
      });
      setGroups(ordered);
      try {
        await reorderProjectActivityGroups(
          projectId,
          ordered.map((group) => group.id)
        );
      } catch (error) {
        console.error(error);
        toast.error(t("project.activities.group_reorder_error"));
        await load();
      }
      return;
    }

    if (result.type !== "ACTIVITY") return;

    const sourceKey = result.source.droppableId;
    const destKey = result.destination.droppableId;
    const destGroupId = parseActivityDroppableId(destKey);
    const destGroup = destGroupId == null
      ? null
      : groups.find((g) => String(g.id) === String(destGroupId)) || null;

    const sourceActs = items
      .filter((row) => activityDroppableId(resolveActivityGroupId(row)) === sourceKey)
      .sort(sortByRowOrder);
    const destActs =
      sourceKey === destKey
        ? sourceActs
        : items
            .filter((row) => activityDroppableId(resolveActivityGroupId(row)) === destKey)
            .sort(sortByRowOrder);

    const [moved] = sourceActs.splice(result.source.index, 1);
    if (!moved) return;
    const updatedMoved = {
      ...moved,
      group_id: destGroupId,
      group: destGroup,
    };
    if (sourceKey === destKey) {
      sourceActs.splice(result.destination.index, 0, updatedMoved);
    } else {
      destActs.splice(result.destination.index, 0, updatedMoved);
    }

    const untouched = items.filter((row) => {
      const key = activityDroppableId(resolveActivityGroupId(row));
      return key !== sourceKey && key !== destKey;
    });
    const next = reindexActivities([
      ...untouched,
      ...sourceActs,
      ...(sourceKey === destKey ? [] : destActs),
    ]);
    setItems(next);
    await persistActivityOrder(next);
  };

  const openNewGroup = () => {
    setDialogGroup(null);
    setGroupDialogOpen(true);
  };

  const openEditGroup = (group) => {
    setDialogGroup(group);
    setGroupDialogOpen(true);
  };

  const handleDeleteGroup = async (group) => {
    if (!canManage || !group?.id) return;
    if (!window.confirm(t("project.activities.group_delete_confirm"))) return;
    try {
      await deleteProjectActivityGroup(projectId, group.id);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      setItems((prev) =>
        prev.map((row) =>
          String(resolveActivityGroupId(row)) === String(group.id)
            ? { ...row, group_id: null, group: null }
            : row
        )
      );
      toast.success(t("project.activities.group_deleted"));
    } catch (error) {
      console.error(error);
      toast.error(t("project.activities.group_delete_error"));
      await load();
    }
  };

  const openDuplicateGroup = (group) => {
    setGroupDupSource(group);
    setGroupDupOpen(true);
  };

  const onGroupDuplicated = () => {
    load();
  };

  const handleDuplicateActivity = async (row) => {
    if (!canManage || !projectId || !row?.id || duplicatingActivityId) return;
    setDuplicatingActivityId(row.id);
    try {
      const created = await duplicateProjectActivity(projectId, row.id);
      if (created?.id) {
        setItems((prev) =>
          [...prev, created].sort(
            (a, b) =>
              (a.row_order || 0) - (b.row_order || 0) || (a.id || 0) - (b.id || 0)
          )
        );
      }
      toast.success(t("project.activities.duplicated"));
      await load();
    } catch (error) {
      console.error(error);
      toast.error(t("project.activities.duplicate_error"));
    } finally {
      setDuplicatingActivityId(null);
    }
  };

  const handleExportPdf = async () => {
    if (!projectId || exportingPdf) return;
    setExportingPdf(true);
    try {
      await exportProjectActivitiesPdf(projectId);
      toast.success(t("project.activities.export_pdf_success"));
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.errors?.[0] ||
        error?.message ||
        t("project.activities.export_pdf_error");
      toast.error(message);
    } finally {
      setExportingPdf(false);
    }
  };

  const toggleGroupCollapsed = (key) => {
    setCollapsedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const startDraftInGroup = (groupId) => {
    setDraftGroupId(groupId);
    setEditMode(true);
    focusDraftTitle();
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const goNewWorkOrder = (row) => {
    navigate("/dashboard/work_orders/new", {
      state: {
        project,
        customer: project?.customer,
        contact: project?.contact,
        delivery_address: project?.delivery_address,
        project_activity: row,
        project_activity_id: row.id,
      },
    });
  };

  const goProjectTab = (tabIndex) => {
    navigate(`/dashboard/projects/${projectId}?tab=${tabIndex}`);
  };

  const inputCls =
    "block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm";

  const fmtNum = (n, frac = 2) =>
    Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: frac,
    });

  const renderGroupBar = (section, { dragHandleProps, showActions = true } = {}) => {
    const collapsed = collapsedGroupKeys.has(section.key);
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 sm:px-3">
        {dragHandleProps ? (
          <DragHandle
            dragHandleProps={dragHandleProps}
            label={t("project.activities.group_drag")}
          />
        ) : null}
        <button
          type="button"
          onClick={() => toggleGroupCollapsed(section.key)}
          className="inline-flex items-center gap-2 min-w-0 flex-1 text-left py-1"
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 shrink-0 text-slate-500" />
          )}
          <span className="text-sm font-semibold text-slate-800 truncate">
            {section.title || t("project_activity.ungrouped")}
          </span>
          <span className="text-xs font-medium text-slate-500">
            ({section.activities.length})
          </span>
        </button>
        {showActions && canManage && !section.isUngrouped ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => startDraftInGroup(section.id)}
              className="p-2 text-[#1463C7] hover:bg-blue-50 rounded-md min-w-[40px] min-h-[40px] inline-flex items-center justify-center"
              title={t("project.activities.group_add_activity")}
            >
              <PlusIcon className="h-5 w-5" />
            </button>
            <Menu as="div" className="relative">
              <Menu.Button
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-md min-w-[40px] min-h-[40px] inline-flex items-center justify-center"
                title={t("project.activities.actions")}
                aria-label={t("project.activities.actions")}
              >
                <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden />
              </Menu.Button>
              <Transition
                as={React.Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-50 mt-1 w-52 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => openEditGroup(section)}
                        className={`${
                          active ? "bg-slate-50" : ""
                        } flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700`}
                      >
                        <PencilIcon className="h-4 w-4" aria-hidden />
                        {t("project.activities.group_edit")}
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => openDuplicateGroup(section)}
                        className={`${
                          active ? "bg-slate-50" : ""
                        } flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700`}
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" aria-hidden />
                        {t("project.activities.group_duplicate")}
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => handleDeleteGroup(section)}
                        className={`${
                          active ? "bg-red-50" : ""
                        } flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600`}
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden />
                        {t("project.activities.group_delete")}
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        ) : null}
      </div>
    );
  };

  const editColSpan = canManage ? 10 : 9;

  const renderEditTable = (section, { showDraft = false } = {}) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="w-12 px-1 py-3" />
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
            >
              {t("project.activities.number")}
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]"
            >
              {t("project.activities.title_field")}
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36"
            >
              {t("project.activities.status_label")}
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]"
            >
              {t("project.activities.description_field")}
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28"
            >
              {t("project.activities.budget_hours")}
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
            >
              {t("project.activities.logged_short")}
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28"
            >
              {t("project.activities.budget_fee")}
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
            >
              {t("project.activities.is_additional")}
            </th>
            {canManage && (
              <th scope="col" className="relative px-3 py-3 w-24">
                <span className="sr-only">{t("project.activities.actions")}</span>
              </th>
            )}
          </tr>
        </thead>
        <Droppable
          droppableId={section.key}
          type="ACTIVITY"
          isDropDisabled={!dndEnabled}
        >
          {(provided, snapshot) => (
            <tbody
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`bg-white divide-y divide-gray-200 ${
                snapshot.isDraggingOver ? "bg-blue-50/40" : ""
              }`}
            >
              {section.activities.map((row, index) => {
                const st =
                  PROJECT_ACTIVITY_STATUSES.includes(row.status) && row.status
                    ? row.status
                    : "open";
                return (
                  <Draggable
                    key={row.id}
                    draggableId={`activity-${row.id}`}
                    index={index}
                    isDragDisabled={!dndEnabled}
                  >
                    {(rowProvided, rowSnapshot) => (
                      <tr
                        ref={rowProvided.innerRef}
                        {...rowProvided.draggableProps}
                        className={`hover:bg-gray-50 ${
                          rowSnapshot.isDragging ? "bg-slate-100 shadow" : ""
                        }`}
                      >
                        <td className="px-1 py-2">
                          {dndEnabled ? (
                            <DragHandle
                              dragHandleProps={rowProvided.dragHandleProps}
                              label={t("project.activities.group_drag")}
                            />
                          ) : (
                            <span className="inline-block w-9" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                          {row.number || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            defaultValue={row.title || ""}
                            key={`t-${row.id}-${row.updated_at || row.id}`}
                            className={inputCls}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v === (row.title || "").trim()) return;
                              if (!v) {
                                toast.error(t("project.activities.title_required"));
                                e.target.value = row.title || "";
                                return;
                              }
                              patchRow(row, { title: v });
                            }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={st}
                            onChange={(e) =>
                              patchRow(row, { status: e.target.value })
                            }
                            className={inputCls}
                          >
                            {PROJECT_ACTIVITY_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {t(`project.activities.status.${s}`)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            defaultValue={row.description || ""}
                            key={`d-${row.id}-${row.updated_at || row.id}`}
                            className={inputCls}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (row.description || "").trim()) {
                                patchRow(row, { description: v || null });
                              }
                            }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            defaultValue={row.budget_hours ?? ""}
                            key={`bh-${row.id}-${row.updated_at || row.id}`}
                            className={inputCls}
                            onBlur={(e) => {
                              const n = normNum(e.target.value);
                              const cur =
                                row.budget_hours != null
                                  ? Number(row.budget_hours)
                                  : null;
                              if (n !== cur) patchRow(row, { budget_hours: n });
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                          {fmtNum(row.logged_hours, 2)}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            defaultValue={row.budget_fee ?? ""}
                            key={`bf-${row.id}-${row.updated_at || row.id}`}
                            className={inputCls}
                            onBlur={(e) => {
                              const n = normNum(e.target.value);
                              const cur =
                                row.budget_fee != null
                                  ? Number(row.budget_fee)
                                  : null;
                              if (n !== cur) patchRow(row, { budget_fee: n });
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={!!row.is_additional}
                            onChange={(e) => {
                              const v = e.target.checked;
                              setItems((prev) =>
                                prev.map((r) =>
                                  r.id === row.id ? { ...r, is_additional: v } : r
                                )
                              );
                              patchRow(row, { is_additional: v });
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        {canManage && (
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => openEditDialog(row)}
                              className="text-indigo-600 hover:text-indigo-800 p-1 inline-flex"
                              title={t("project.activities.status_progress_title")}
                            >
                              <ChartBarIcon className="h-5 w-5" />
                            </button>
                            <Menu as="div" className="relative inline-block text-left">
                              <Menu.Button
                                className="text-slate-600 hover:text-slate-800 p-1 inline-flex"
                                title={t("project.activities.actions")}
                                aria-label={t("project.activities.actions")}
                              >
                                <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden />
                              </Menu.Button>
                              <Transition
                                as={React.Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className="absolute right-0 z-50 mt-1 w-48 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        type="button"
                                        disabled={duplicatingActivityId === row.id}
                                        onClick={() => handleDuplicateActivity(row)}
                                        className={`${
                                          active ? "bg-slate-50" : ""
                                        } flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 disabled:opacity-60`}
                                      >
                                        <DocumentDuplicateIcon
                                          className="h-4 w-4"
                                          aria-hidden
                                        />
                                        {duplicatingActivityId === row.id
                                          ? t("project.activities.duplicating")
                                          : t("project.activities.duplicate")}
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        type="button"
                                        onClick={() => handleDelete(row)}
                                        className={`${
                                          active ? "bg-red-50" : ""
                                        } flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600`}
                                      >
                                        <TrashIcon className="h-4 w-4" aria-hidden />
                                        {t("project.activities.delete")}
                                      </button>
                                    )}
                                  </Menu.Item>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          </td>
                        )}
                      </tr>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
              {showDraft ? (
                <tr className="bg-indigo-50/40">
                  <td className="px-1 py-2" />
                  <td className="px-3 py-2 text-sm text-gray-500 align-middle">
                    —
                  </td>
                  <td className="px-3 py-2">
                    <input
                      ref={draftTitleRef}
                      type="text"
                      value={draft.title}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, title: e.target.value }))
                      }
                      onBlur={saveDraftFromTitleBlur}
                      placeholder={t("project.activities.draft_title_placeholder")}
                      className={inputCls}
                    />
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-400">—</td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={draft.description}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          description: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.01"
                      value={draft.budget_hours}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          budget_hours: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-sm">—</td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.01"
                      value={draft.budget_fee}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          budget_fee: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={draft.is_additional}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          is_additional: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-3 py-2 text-center align-middle">
                    <span
                      className="inline-flex text-indigo-500"
                      title={t("project.activities.add_quick")}
                    >
                      <PlusIcon className="h-6 w-6 mx-auto" aria-hidden />
                    </span>
                  </td>
                </tr>
              ) : section.activities.length === 0 ? (
                <tr>
                  <td
                    colSpan={editColSpan}
                    className="px-4 py-4 text-center text-sm text-slate-400"
                  >
                    {t("project.activities.group_empty")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          )}
        </Droppable>
      </table>
    </div>
  );

  if (!projectId) return null;

  return (
    <div className="space-y-4 bg-white px-4 py-6 sm:px-6 rounded-lg shadow sm:rounded-md">
      <div className="rounded-lg border border-gray-200 bg-slate-50/80 p-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">
          {t("project.activities.summary_title")}
        </h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("project.activities.summary_hours")}
            </div>
            <div className="text-sm text-gray-800">
              {fmtNum(aggregates.totalLogged, 2)} /{" "}
              {aggregates.hasBudgetHours ? fmtNum(aggregates.totalBudgetHours, 2) : "—"}{" "}
              {t("project.activities.hours_unit")}
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-2 rounded-full transition-all ${
                  aggregates.hoursBarPct != null && aggregates.hoursBarPct > 100
                    ? "bg-amber-500"
                    : "bg-indigo-600"
                }`}
                style={{
                  width: `${
                    aggregates.hoursBarPct != null
                      ? Math.min(100, aggregates.hoursBarPct)
                      : 0
                  }%`,
                }}
              />
            </div>
            {aggregates.hoursBarPct != null && (
              <div className="text-xs text-gray-500">
                {Math.min(100, aggregates.hoursBarPct).toFixed(1)}%{" "}
                {t("project.activities.of_total_budget_hours")}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("project.activities.summary_activities_done")}
            </div>
            <div className="text-sm text-gray-800">
              {aggregates.completed} / {aggregates.total}{" "}
              {t("project.activities.completed_label")}
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-emerald-600 transition-all"
                style={{ width: `${aggregates.doneBarPct}%` }}
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("project.activities.summary_budget_fee")}
            </div>
            <div className="text-sm text-gray-800">
              {aggregates.hasFee
                ? fmtNum(aggregates.totalBudgetFee, 2)
                : "—"}{" "}
              {aggregates.hasFee ? t("project.activities.currency_nok") : ""}
            </div>
            <p className="text-xs text-gray-500">
              {t("project.activities.summary_budget_fee_hint")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {t("project.activities.search_label")}
            </label>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("project.activities.search_placeholder")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {t("project.activities.filter_label")}
            </label>
            <select
              value={filterKind}
              onChange={(e) => setFilterKind(e.target.value)}
              className={inputCls}
            >
              <option value="all">{t("project.activities.filter_all")}</option>
              <option value="standard">
                {t("project.activities.filter_standard")}
              </option>
              <option value="additional">
                {t("project.activities.filter_additional")}
              </option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exportingPdf || !projectId}
            className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-[#1463C7]" aria-hidden />
            {exportingPdf
              ? t("project.activities.export_pdf_loading")
              : t("project.activities.export_pdf")}
          </button>
          {canManage && (
            <button
              type="button"
              onClick={openNewGroup}
              className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <PlusIcon className="h-5 w-5 mr-2 text-[#1463C7]" aria-hidden />
              {t("project.activities.group_new")}
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => setEditMode((v) => !v)}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              {editMode ? (
                <>
                  <EyeIcon className="h-5 w-5 mr-2 text-gray-600" aria-hidden />
                  {t("project.activities.mode_view")}
                </>
              ) : (
                <>
                  <PencilSquareIcon
                    className="h-5 w-5 mr-2 text-gray-600"
                    aria-hidden
                  />
                  {t("project.activities.mode_edit")}
                </>
              )}
            </button>
          )}
          {canManage && editMode && (
            <button
              type="button"
              onClick={() => {
                setDraftGroupId(null);
                focusDraftTitle();
              }}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" aria-hidden />
              {t("project.activities.add_quick")}
            </button>
          )}
        </div>
      </div>

      {!editMode && loading && (
        <div className="py-12 text-center text-gray-500">{t("Loading")}…</div>
      )}

      {!editMode && !loading && (
        <div className="space-y-4">
          {filteredItems.length === 0 && !showGroups ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {t("project.activities.none")}
            </p>
          ) : (
            (showGroups
              ? groupedSections
              : [{ key: "flat", activities: filteredItems, skipHeader: true }]
            ).map((section) => (
              <div key={section.key} className="space-y-2">
                {!section.skipHeader ? renderGroupBar(section) : null}
                {collapsedGroupKeys.has(section.key)
                  ? null
                  : section.activities.length === 0 && !section.skipHeader ? (
                      <p className="text-sm text-slate-400 px-3 py-2">
                        {t("project.activities.group_empty")}
                      </p>
                    ) : (
            section.activities.map((row) => {
              const expanded = expandedIds.has(row.id);
              const hp = hoursPercent(row);
              const st =
                PROJECT_ACTIVITY_STATUSES.includes(row.status) && row.status
                  ? row.status
                  : "open";
              const logged = Number(row.logged_hours) || 0;
              const budg =
                row.budget_hours != null ? Number(row.budget_hours) : null;

              return (
                <div
                  key={row.id}
                  className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  <div className="flex flex-wrap items-center gap-2 px-3 py-3 sm:px-4">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(row.id)}
                      className="flex items-center gap-2 text-left min-w-0 flex-1"
                      aria-expanded={expanded}
                    >
                      {expanded ? (
                        <ChevronDownIcon className="h-5 w-5 shrink-0 text-gray-500" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-500" />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          <span className="text-gray-500 mr-2">
                            {row.number || "—"}
                          </span>
                          {row.display_title || row.title}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(
                              st
                            )}`}
                          >
                            {t(`project.activities.status.${st}`)}
                          </span>
                          <span className="text-xs text-gray-600">
                            {fmtNum(logged, 2)} /{" "}
                            {budg != null && Number.isFinite(budg)
                              ? fmtNum(budg, 2)
                              : "—"}{" "}
                            {t("project.activities.hours_unit_short")}
                          </span>
                          {hp != null && (
                            <span className="text-xs text-indigo-700 font-medium">
                              {hp.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      {canManage && (
                        <>
                          <button
                            type="button"
                            onClick={() => openEditDialog(row)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md"
                            title={t("project.activities.edit")}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditDialog(row)}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-md"
                            title={t("project.activities.status_progress_title")}
                          >
                            <ChartBarIcon className="h-5 w-5" />
                          </button>
                          <Menu as="div" className="relative">
                            <Menu.Button
                              className="p-2 text-slate-600 hover:bg-slate-100 rounded-md"
                              title={t("project.activities.actions")}
                              aria-label={t("project.activities.actions")}
                            >
                              <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden />
                            </Menu.Button>
                            <Transition
                              as={React.Fragment}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <Menu.Items className="absolute right-0 z-50 mt-1 w-48 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      type="button"
                                      disabled={duplicatingActivityId === row.id}
                                      onClick={() => handleDuplicateActivity(row)}
                                      className={`${
                                        active ? "bg-slate-50" : ""
                                      } flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 disabled:opacity-60`}
                                    >
                                      <DocumentDuplicateIcon
                                        className="h-4 w-4"
                                        aria-hidden
                                      />
                                      {duplicatingActivityId === row.id
                                        ? t("project.activities.duplicating")
                                        : t("project.activities.duplicate")}
                                    </button>
                                  )}
                                </Menu.Item>
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        </>
                      )}
                    </div>
                  </div>
                  {expanded && (
                    <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4 space-y-4 text-sm">
                      {row.description ? (
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {row.description}
                        </p>
                      ) : (
                        <p className="text-gray-400 italic">
                          {t("project.activities.no_description")}
                        </p>
                      )}
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-600">
                        <div>
                          <dt className="text-xs text-gray-500">
                            {t("project.activities.budget_fee")}
                          </dt>
                          <dd>
                            {row.budget_fee != null && row.budget_fee !== ""
                              ? fmtNum(row.budget_fee, 2)
                              : "—"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">
                            {t("project.activities.is_additional")}
                          </dt>
                          <dd>{row.is_additional ? t("Yes") : t("No")}</dd>
                        </div>
                        {(row.start_date || row.end_date) && (
                          <div className="sm:col-span-2">
                            <dt className="text-xs text-gray-500">
                              {t("project.activities.period")}
                            </dt>
                            <dd>
                              {row.start_date || "—"} → {row.end_date || "—"}
                            </dd>
                          </div>
                        )}
                        {projectActivityLine(row.activity_item) && (
                          <div className="sm:col-span-2">
                            <dt className="text-xs text-gray-500">
                              {t("project.activities.activity_line")}
                            </dt>
                            <dd>{projectActivityLine(row.activity_item)}</dd>
                          </div>
                        )}
                        <div className="sm:col-span-2">
                          <dt className="text-xs text-gray-500">
                            {t("project.activities.linked_work_orders_label")}
                          </dt>
                          <dd>{row.linked_work_orders_count ?? 0}</dd>
                        </div>
                      </dl>
                      <div className="flex flex-wrap gap-2 pt-2">
                        {workOrderAccess.canCreate && (
                          <button
                            type="button"
                            onClick={() => goNewWorkOrder(row)}
                            className="inline-flex items-center rounded-md border border-transparent bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700"
                          >
                            <WrenchScrewdriverIcon className="h-4 w-4 mr-1.5" />
                            {t("project.activities.new_work_order")}
                          </button>
                        )}
                        {workOrderAccess.canRead && (
                          <button
                            type="button"
                            onClick={() => goProjectTab(workOrdersTabIndex)}
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <WrenchScrewdriverIcon className="h-4 w-4 mr-1.5" />
                            {t("project.activities.open_work_orders_tab")}
                          </button>
                        )}
                        {projectPermissions.canRead && (
                          <button
                            type="button"
                            onClick={() => goProjectTab(timeRegistrationTabIndex)}
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <ClockIcon className="h-4 w-4 mr-1.5" />
                            {t("project.activities.open_time_tab")}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
                    )}
              </div>
            ))
          )}
        </div>
      )}

      {editMode && (
        <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-4">
          {loading ? (
            <div className="py-8 text-center text-gray-500">{t("Loading")}…</div>
          ) : (
            <>
              <Droppable
                droppableId="activity-groups"
                type="GROUP"
                isDropDisabled={!dndEnabled || !showGroups}
              >
                {(groupListProvided) => (
                  <div
                    ref={groupListProvided.innerRef}
                    {...groupListProvided.droppableProps}
                    className="space-y-4"
                  >
                    {realGroupSections.map((section, groupIndex) => (
                      <Draggable
                        key={section.key}
                        draggableId={section.key}
                        index={groupIndex}
                        isDragDisabled={!dndEnabled}
                      >
                        {(groupProvided, groupSnapshot) => (
                          <div
                            ref={groupProvided.innerRef}
                            {...groupProvided.draggableProps}
                            className={`rounded-lg border border-slate-200 overflow-hidden ${
                              groupSnapshot.isDragging
                                ? "bg-slate-50 shadow-md"
                                : "bg-white"
                            }`}
                          >
                            {renderGroupBar(section, {
                              dragHandleProps: dndEnabled
                                ? groupProvided.dragHandleProps
                                : null,
                            })}
                            {!collapsedGroupKeys.has(section.key) &&
                              renderEditTable(section, {
                                showDraft:
                                  draftGroupId != null &&
                                  String(draftGroupId) === String(section.id),
                              })}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {groupListProvided.placeholder}
                  </div>
                )}
              </Droppable>
              <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                {showGroups
                  ? renderGroupBar(ungroupedSection, { showActions: false })
                  : null}
                {(!showGroups || !collapsedGroupKeys.has(ungroupedSection.key)) &&
                  renderEditTable(ungroupedSection, {
                    showDraft: draftGroupId == null,
                  })}
              </div>
            </>
          )}
        </div>
        </DragDropContext>
      )}

      {canManage && editMode && (
        <p className="text-xs text-gray-500">{t("project.activities.tab_hint")}</p>
      )}

      <ProjectActivityEditDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setDialogActivity(null);
        }}
        projectId={projectId}
        activity={dialogActivity}
        onSaved={onDialogSaved}
      />

      <ProjectActivityGroupDialog
        open={groupDialogOpen}
        onClose={() => {
          setGroupDialogOpen(false);
          setDialogGroup(null);
        }}
        projectId={projectId}
        group={dialogGroup}
        nextRowOrder={groups.length}
        onSaved={load}
      />

      <ProjectActivityGroupDuplicateDialog
        open={groupDupOpen}
        onClose={() => {
          setGroupDupOpen(false);
          setGroupDupSource(null);
        }}
        projectId={projectId}
        group={groupDupSource}
        onDuplicated={onGroupDuplicated}
      />
    </div>
  );
};
